/**
 * SystemStatus — Iter 122 sidebar chip.
 *
 * Pings /api/health every 60 s and shows a color-coded dot in the sidebar
 * footer. Click to expand for last-check timestamp + latency + any error.
 *
 *   🟢 green    healthy   (200, latency ≤ 800 ms)
 *   🟡 amber    slow      (200, latency > 800 ms)
 *   🔴 red      error     (network error / non-2xx / timeout)
 *
 * Rationale: gives clinic owners an immediate, silent signal when the
 * backend has a hiccup (Cloudflare 502, cold-start, network blip) rather
 * than them opening a support ticket that says "something's broken".
 */
import React, { useEffect, useRef, useState } from 'react';
import { API } from '../lib/api';
import { ChevronDown, ChevronUp } from 'lucide-react';

const POLL_INTERVAL_MS = 60_000;
const REQUEST_TIMEOUT_MS = 8_000;
const SLOW_THRESHOLD_MS = 800;

const LEVELS = {
  ok:      { color: '#3A9E5C', dot: 'bg-[#3A9E5C]' },
  slow:    { color: '#DA8B00', dot: 'bg-[#DA8B00]' },
  error:   { color: '#B83A3A', dot: 'bg-[#B83A3A]' },
  loading: { color: '#7A7E78', dot: 'bg-[#7A7E78] animate-pulse' },
};

// Iter 122 — the labels are rendered INLINE in JSX via conditional
// expressions with runtime string joins. This is deliberate: the Babel
// `visual-edits` plugin freezes any static string literals inside JSX at
// build time and never re-renders them on state change (colour props
// bound to `style` are unaffected, but text nodes are). Using
// `Array.prototype.join` forces the plugin to treat each label as a
// dynamic expression it can't precompute.

export default function SystemStatus() {
  const [state, setState] = useState({ level: 'loading', latencyMs: null, checkedAt: null, error: null });
  const [expanded, setExpanded] = useState(false);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    let cancelled = false;

    const ping = async () => {
      const started = performance.now();
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
      try {
        // Bypass axios so we don't hit auth interceptors / retries — plain fetch,
        // no credentials, no cache. /api/health is public.
        const r = await fetch(`${API}/health`, {
          method: 'GET',
          cache: 'no-store',
          signal: controller.signal,
        });
        const latencyMs = Math.round(performance.now() - started);
        if (cancelled || !mountedRef.current) return;
        if (r.ok) {
          setState({
            level: latencyMs > SLOW_THRESHOLD_MS ? 'slow' : 'ok',
            latencyMs, checkedAt: Date.now(), error: null,
          });
        } else {
          setState({
            level: 'error', latencyMs, checkedAt: Date.now(),
            error: `HTTP ${r.status}`,
          });
        }
      } catch (e) {
        if (cancelled || !mountedRef.current) return;
        const latencyMs = Math.round(performance.now() - started);
        setState({
          level: 'error', latencyMs, checkedAt: Date.now(),
          error: e.name === 'AbortError' ? 'Timeout (>8s)' : (e.message || 'Network error'),
        });
      } finally {
        clearTimeout(timer);
      }
    };

    ping();
    const id = setInterval(ping, POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      mountedRef.current = false;
      clearInterval(id);
    };
  }, []);

  const meta = LEVELS[state.level];
  const timeStr = state.checkedAt
    ? new Date(state.checkedAt).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', second: '2-digit' })
    : '—';

  return (
    <div className="text-[11px] rounded-md border border-soft px-2 py-1.5 bg-white/40" data-testid="system-status">
      <button
        type="button"
        onClick={() => setExpanded((x) => !x)}
        className="flex items-center justify-between w-full gap-2 text-fg-muted hover:text-fg transition"
        aria-label={['System', 'status', state.level].join(' ')}
        data-testid="system-status-toggle"
      >
        <span className="inline-flex items-center gap-2">
          <span className={`inline-block w-2 h-2 rounded-full ${meta.dot}`} aria-hidden="true"/>
          <span className="font-medium" style={{ color: meta.color }} data-testid="system-status-label">
            {state.level === 'ok'   && (['All', 'systems', 'normal'].join(' '))}
            {state.level === 'slow' && (['Backend', 'is', 'slow'].join(' '))}
            {state.level === 'error' && (['Backend', 'unreachable'].join(' '))}
            {state.level === 'loading' && 'Checking…'}
          </span>
        </span>
        {expanded ? <ChevronUp className="w-3 h-3"/> : <ChevronDown className="w-3 h-3"/>}
      </button>
      {expanded && (
        <div className="mt-1.5 pt-1.5 border-t border-soft space-y-0.5 text-fg-muted" data-testid="system-status-detail">
          <div className="flex justify-between"><span>Last check</span><span className="font-mono">{timeStr}</span></div>
          {state.latencyMs != null && (
            <div className="flex justify-between"><span>Latency</span><span className="font-mono">{state.latencyMs} ms</span></div>
          )}
          {state.error && (
            <div className="flex justify-between text-[#B83A3A]"><span>Error</span><span className="font-mono truncate max-w-[110px]" title={state.error}>{state.error}</span></div>
          )}
          <div className="text-[10px] text-fg-muted/70 pt-0.5">Auto-refresh every 60 s.</div>
        </div>
      )}
    </div>
  );
}
